import { useEffect, useRef, useState } from "react";
import { ImageIcon, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { resolveSignedStorageUrls, resolveStoredObjectUrl, resolveStoredObjectUrlAsync } from "@/lib/storageObjectUrls";
import { cn } from "@/lib/utils";

interface PhotoUploadButtonProps {
  bucket: string;
  onPhotoReady: (url: string) => void;
  onPhotoPreviewReady?: (url: string | null) => void;
  onClear: () => void;
  previewUrl: string | null;
  multiple?: boolean;
  onMultipleReady?: (urls: string[]) => void;
  existingPhotos?: string[];
  onRemoveExisting?: (index: number) => void;
  buttonLabel?: string;
  buttonClassName?: string;
  returnObjectPath?: boolean;
}

const EMPTY_EXISTING_PHOTOS: string[] = [];

const PhotoUploadButton = ({
  bucket,
  onPhotoReady,
  onPhotoPreviewReady,
  onClear,
  previewUrl,
  multiple = false,
  onMultipleReady,
  existingPhotos = EMPTY_EXISTING_PHOTOS,
  onRemoveExisting,
  buttonLabel,
  buttonClassName,
  returnObjectPath = false,
}: PhotoUploadButtonProps) => {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [resolvedExistingPhotos, setResolvedExistingPhotos] = useState<Map<string, string>>(new Map());
  const [resolvedPreviewUrl, setResolvedPreviewUrl] = useState<string | null>(previewUrl);

  useEffect(() => {
    let active = true;

    void resolveSignedStorageUrls(bucket, existingPhotos).then((nextMap) => {
      if (active) {
        setResolvedExistingPhotos(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [bucket, existingPhotos]);

  useEffect(() => {
    let active = true;

    if (!previewUrl) {
      setResolvedPreviewUrl(null);
      return () => {
        active = false;
      };
    }

    void resolveStoredObjectUrlAsync(bucket, previewUrl).then((nextUrl) => {
      if (active) {
        setResolvedPreviewUrl(nextUrl);
      }
    });

    return () => {
      active = false;
    };
  }, [bucket, previewUrl]);

  const uploadFile = async (file: File): Promise<string | null> => {
    if (!user) return null;
    const ts = Date.now();
    const path = `${user.id}/${ts}_${file.name}`;
    const { error } = await supabase.storage.from(bucket).upload(path, file, { contentType: file.type });
    if (error) return null;

    if (returnObjectPath) {
      if (onPhotoPreviewReady) {
        const { data, error: signedError } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60);
        onPhotoPreviewReady(signedError ? null : data?.signedUrl ?? null);
      }
      return path;
    }

    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      if (multiple && onMultipleReady) {
        const urls: string[] = [];
        for (let i = 0; i < Math.min(files.length, 4 - existingPhotos.length); i++) {
          const url = await uploadFile(files[i]);
          if (url) urls.push(url);
        }
        onMultipleReady(urls);
      } else {
        const url = await uploadFile(files[0]);
        if (url) onPhotoReady(url);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Existing photos */}
      {existingPhotos.map((url, i) => (
        <div key={i} className="relative h-12 w-16 shrink-0 rounded-lg overflow-hidden">
          <img src={resolveStoredObjectUrl(bucket, url, resolvedExistingPhotos) ?? url} alt="" className="h-full w-full object-cover" />
          {onRemoveExisting && (
            <button onClick={() => onRemoveExisting(i)} className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5">
              <X size={10} className="text-white" />
            </button>
          )}
        </div>
      ))}

      {/* Single preview */}
      {!multiple && resolvedPreviewUrl && (
        <div className="relative h-12 w-16 shrink-0 rounded-lg overflow-hidden">
          <img src={resolvedPreviewUrl} alt="" className="h-full w-full object-cover" />
          <button onClick={onClear} className="absolute top-0.5 right-0.5 rounded-full bg-black/60 p-0.5">
            <X size={10} className="text-white" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading || (!multiple && !!resolvedPreviewUrl) || (multiple && existingPhotos.length >= 4)}
        className={cn(
          buttonLabel
            ? "inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:border-primary hover:text-primary disabled:opacity-40"
            : "flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-primary disabled:opacity-40",
          buttonClassName
        )}
      >
        {uploading ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        ) : (
          <ImageIcon size={20} />
        )}
        {buttonLabel ? <span>{buttonLabel}</span> : null}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
};

export default PhotoUploadButton;
